import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Mail, User, Shield, Link2, Unlink, Key, UserPlus, Eye, EyeOff, Lock, Send } from "lucide-react";
import { EmployeeExternalCredentials } from "./EmployeeExternalCredentials";
import { Checkbox } from "@/components/ui/checkbox";
import ChangePasswordDialog from "./ChangePasswordDialog";

interface EmployeeCredentialsFormProps {
  employeeId: string;
  readOnly?: boolean;
}

interface LinkedProfile {
  id: string;
  email: string;
  full_name: string | null;
}


export function EmployeeCredentialsForm({ employeeId, readOnly = false }: EmployeeCredentialsFormProps) {
  const { user, userRole, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [linkedProfile, setLinkedProfile] = useState<LinkedProfile | null>(null);
  const [linkedUserRole, setLinkedUserRole] = useState<string | null>(null);
  const [employeeName, setEmployeeName] = useState("");
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [resendingCredentials, setResendingCredentials] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  
  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("employee");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [customPassword, setCustomPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Fixed role-based permission: admin, hr_manager, hr_clerk can reset passwords
  const canManage = !readOnly && (userRole === "admin" || userRole === "hr_manager" || userRole === "hr_clerk");
  
  // Only Admins and HR Managers can create new users (invite) - hr_clerk can only reset passwords
  const canInvite = !readOnly && (userRole === "admin" || userRole === "hr_manager");
  
  // Check if current user is viewing their own profile (linked via employee_id)
  // This must match the current auth user's profile, not just any linked profile
  const isOwnProfile = profile?.employee_id === employeeId && profile?.employee_id !== null;

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);

  const loadData = async () => {
    try {
      const { data: empData, error: empError } = await supabase
        .from("employees")
        .select("name, private_email")
        .eq("id", employeeId)
        .single();

      if (empError) throw empError;
      setEmployeeName(empData.name);
      setEmployeeEmail(empData.private_email || "");
      setInviteEmail(empData.private_email || "");

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, email, full_name")
        .eq("employee_id", employeeId)
        .maybeSingle();

      if (profileError) throw profileError;

      if (profileData) {
        setLinkedProfile(profileData);

        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", profileData.id)
          .maybeSingle();

        if (!roleError && roleData) {
          setLinkedUserRole(roleData.role);
        }
      } else {
        setLinkedProfile(null);
      }
    } catch (error) {
      console.error("Error loading credentials data:", error);
      toast.error("Fehler beim Laden der Zugangsdaten");
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!linkedProfile?.id || !linkedProfile?.email) {
      toast.error("Keine Benutzerinformationen vorhanden");
      return;
    }

    setSendingReset(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke("reset-password", {
        body: {
          userId: linkedProfile.id,
          email: linkedProfile.email,
          fullName: linkedProfile.full_name || employeeName
        },
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`
        }
      });

      if (response.error) {
        throw new Error(response.error.message || "Fehler beim Zurücksetzen");
      }

      const result = response.data;
      
      if (result.tempPassword) {
        // Email failed, show password to admin
        toast.warning(`E-Mail konnte nicht gesendet werden. Temporäres Passwort: ${result.tempPassword}`, {
          duration: 10000
        });
      } else {
        toast.success(`Neues Passwort wurde an ${linkedProfile.email} gesendet. Der Mitarbeiter muss es nach dem Login ändern.`);
      }
    } catch (error: any) {
      console.error("Error sending password reset:", error);
      toast.error(error.message || "Fehler beim Zurücksetzen des Passworts");
    } finally {
      setSendingReset(false);
    }
  };

  const handleResendCredentials = async () => {
    if (!linkedProfile?.id || !linkedProfile?.email) {
      toast.error("Keine Benutzerinformationen vorhanden");
      return;
    }

    setResendingCredentials(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke("invite-user", {
        body: {
          email: linkedProfile.email,
          fullName: linkedProfile.full_name || employeeName,
          employeeId: employeeId,
          role: linkedUserRole || "employee",
          createdBy: user?.id,
          resend: true
        },
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`
        }
      });

      if (response.error) {
        throw new Error(response.error.message || "Fehler beim erneuten Senden");
      }

      const result = response.data;
      
      if (result.tempPassword) {
        toast.warning(`E-Mail konnte nicht gesendet werden. Temporäres Passwort: ${result.tempPassword}`, {
          duration: 10000
        });
      } else {
        toast.success(`Zugangsdaten wurden erneut an ${linkedProfile.email} gesendet.`);
      }
    } catch (error: any) {
      console.error("Error resending credentials:", error);
      toast.error(error.message || "Fehler beim erneuten Senden der Zugangsdaten");
    } finally {
      setResendingCredentials(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error("Bitte geben Sie eine E-Mail-Adresse ein");
      return;
    }

    if (useCustomPassword && customPassword.length < 8) {
      toast.error("Das Passwort muss mindestens 8 Zeichen haben");
      return;
    }

    setSendingInvite(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke("invite-user", {
        body: {
          email: inviteEmail,
          fullName: employeeName,
          employeeId: employeeId,
          role: inviteRole,
          createdBy: user?.id,
          ...(useCustomPassword && customPassword ? { customPassword } : {})
        },
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`
        }
      });

      if (response.error) {
        throw new Error(response.error.message || "Fehler beim Einladen");
      }

      const result = response.data;
      
      if (result.existingUser) {
        toast.success("Bestehender Benutzer wurde mit diesem Mitarbeiter verknüpft");
      } else if (result.passwordSet) {
        toast.success(`Benutzer erstellt mit dem angegebenen Passwort.`);
      } else if (result.isNewUser) {
        toast.success(`Einladung an ${inviteEmail} gesendet. Der Mitarbeiter erhält eine E-Mail mit den Zugangsdaten.`);
      } else {
        toast.success(result.message || "Zugang erfolgreich eingerichtet");
      }
      
      setShowInviteForm(false);
      setUseCustomPassword(false);
      setCustomPassword("");
      loadData();
    } catch (error: any) {
      console.error("Error sending invite:", error);
      toast.error(error.message || "Fehler beim Senden der Einladung");
    } finally {
      setSendingInvite(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 border-red-200";
      case "hr_manager":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "hr_clerk":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "hr_clerk":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "employee":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin":
        return "Administrator";
      case "hr_manager":
        return "Personalmanager";
      case "hr_clerk":
        return "Personalverwalter";
      case "hr_clerk":
        return "Personalverwalter";
      case "employee":
        return "Mitarbeiter";
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Access Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Systemzugang
          </CardTitle>
          <CardDescription>
            Benutzerkonto für {employeeName}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {linkedProfile ? (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <Link2 className="h-5 w-5 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{linkedProfile.full_name || "Kein Name"}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3 shrink-0" />
                    <span className="truncate">{linkedProfile.email}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {linkedUserRole && (
                    <Badge className={getRoleBadgeColor(linkedUserRole)}>
                      <Shield className="h-3 w-3 mr-1" />
                      {getRoleLabel(linkedUserRole)}
                    </Badge>
                  )}
                </div>
              </div>

              {canManage && (
                <div className="pt-2 flex flex-wrap gap-2">
                  <div>
                    <Button
                      variant="outline"
                      onClick={handleSendPasswordReset}
                      disabled={sendingReset || resendingCredentials}
                    >
                      {sendingReset ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Key className="mr-2 h-4 w-4" />
                          Passwort zurücksetzen
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Setzt das Passwort zurück und sendet ein neues temporäres Passwort per E-Mail.
                    </p>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      onClick={handleResendCredentials}
                      disabled={resendingCredentials || sendingReset}
                    >
                      {resendingCredentials ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Zugangsdaten erneut senden
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Generiert ein neues Passwort und sendet die vollständigen Zugangsdaten erneut per E-Mail.
                    </p>
                  </div>
                </div>
              )}

              {/* Own password change section */}
              {isOwnProfile && (
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setChangePasswordOpen(true)}
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    Passwort ändern
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Ändern Sie Ihr eigenes Passwort für mehr Sicherheit.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg border-2 border-dashed">
                <Unlink className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="font-medium text-muted-foreground">Kein Benutzerkonto vorhanden</p>
                  <p className="text-sm text-muted-foreground">
                    Dieser Mitarbeiter hat noch keinen System-Zugang.
                  </p>
                </div>
              </div>

              {canInvite && !showInviteForm && (
                <Button onClick={() => setShowInviteForm(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Zugang einrichten
                </Button>
              )}

              {canInvite && showInviteForm && (
                <div className="p-4 border rounded-lg space-y-4 bg-background">
                  <div className="space-y-2">
                    <Label>E-Mail-Adresse *</Label>
                    <Input
                      type="email"
                      placeholder="E-Mail-Adresse eingeben..."
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Eine Einladung mit temporärem Passwort wird an diese Adresse gesendet.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Rolle *</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Mitarbeiter</SelectItem>
                        <SelectItem value="hr_clerk">Personalverwalter</SelectItem>
                        <SelectItem value="hr_manager">Personalmanager</SelectItem>
                        {userRole === "admin" && (
                          <SelectItem value="admin">Administrator</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom Password Option */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="useCustomPassword"
                        checked={useCustomPassword}
                        onCheckedChange={(checked) => {
                          setUseCustomPassword(checked as boolean);
                          if (!checked) setCustomPassword("");
                        }}
                      />
                      <Label htmlFor="useCustomPassword" className="text-sm font-normal cursor-pointer">
                        Passwort manuell vergeben (keine E-Mail)
                      </Label>
                    </div>

                    {useCustomPassword && (
                      <div className="space-y-2">
                        <Label>Passwort *</Label>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Passwort eingeben (min. 8 Zeichen)..."
                            value={customPassword}
                            onChange={(e) => setCustomPassword(e.target.value)}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Das Passwort wird dem Benutzer nicht per E-Mail zugesendet. Sie müssen es manuell weitergeben.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSendInvite} disabled={sendingInvite}>
                      {sendingInvite ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Wird erstellt...
                        </>
                      ) : useCustomPassword ? (
                        <>
                          <Key className="mr-2 h-4 w-4" />
                          Benutzer erstellen
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Einladung senden
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowInviteForm(false);
                        setUseCustomPassword(false);
                        setCustomPassword("");
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* External Credentials */}
      <EmployeeExternalCredentials employeeId={employeeId} readOnly={readOnly} />

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={changePasswordOpen} 
        onOpenChange={setChangePasswordOpen} 
      />
    </div>
  );
}